// Core packages
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AfterViewInit, Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { DocumentTemplateDocsDialogComponent } from "src/app/modules/settings/components/document-template-docs-dialog/document-template-docs-dialog.component";
import {
  DocumentTemplateMoveDialogComponent,
  DocumentTemplateMoveDialogData,
} from "src/app/modules/settings/components/document-template-move-dialog/document-template-move-dialog.component";
import {
  DocumentTemplateFieldFactory,
  FieldOption,
  LabelConflictChecker,
} from "src/app/modules/settings/document-template.fields";
import { DocumentTemplateService } from "src/app/modules/settings/document-template.service";
import {
  findLabelConflict,
  LabelConflict,
  suggestAreaCode,
} from "src/app/modules/settings/document-template.utils";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import {
  DocumentTemplateArea,
  DocumentTemplateAreaUpdatePayload,
  DocumentTemplateContent,
  DocumentTemplateContentUpdatePayload,
  DocumentTemplateDeleteResult,
  DocumentTemplateStructureOption,
  DocumentTemplateTreeArea,
} from "src/app/shared/interfaces/document-template.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { ThemeService } from "src/app/shared/services/theme.service";

/** Which editor the right column is showing */
type RightPanel = "empty" | "area" | "newArea";

/**
 * Document template management, three-collections back-end
 * (/api/v2/document-template: areas -> contents -> docs).
 *
 * Loading is per level and never fetches the whole tree to feed the UI:
 * areas on mount, sections when an area is picked, documents when the dialog opens.
 * Ordering is persisted on drop through the `position` field, so the lists have no
 * "save" button of their own.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-document-template",
  templateUrl: "./document-template.page.html",
  styleUrls: ["./document-template.page.scss"],
})
export class DocumentTemplateComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[] = [];
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Level 1 state
  areas: DocumentTemplateArea[] = [];
  visibleAreas: DocumentTemplateArea[] = [];
  areaSearch = "";
  selectedArea: DocumentTemplateArea = null;
  areasAreLoading = true;
  // END - Level 1 state

  // BEGIN - Level 2 state
  contents: DocumentTemplateContent[] = [];
  contentsAreLoading = false;
  // END - Level 2 state

  // BEGIN - Duplicated labels
  /**
   * Snapshots the label uniqueness check runs against, HIDDEN ROWS INCLUDED: the
   * back-end refuses a label held by a soft deleted node too, and that node is exactly
   * the one the user cannot see in the tree.
   *
   * They are NOT the lists being displayed: those follow the `includeDeleted` filter.
   */
  private areasForValidation: DocumentTemplateArea[] = [];
  private contentsForValidation: DocumentTemplateContent[] = [];
  /** Set when a snapshot could not be fetched: the check degrades, it never lies */
  areaCheckIsDegraded = false;
  contentCheckIsDegraded = false;
  // END - Duplicated labels

  /** Hidden (soft deleted) nodes are requested with includeDeleted=true */
  includeDeleted = false;

  /** Blocks drag&drop while a reorder is being persisted */
  reorderIsSaving = false;

  /** Blocks the destructive actions while their preview is being computed */
  previewIsLoading = false;

  /** Set while the destinations of a move are being fetched */
  moveIsLoading = false;

  rightPanel: RightPanel = "empty";
  newContentIsVisible = false;

  /** Identifiers (code + title) are read only until the user explicitly unlocks them */
  areaIdentifiersUnlocked = false;

  condoStructures: DocumentTemplateStructureOption[] = [];

  /** Builders of the Formly fields shared with the third level dialog */
  private fieldFactory = new DocumentTemplateFieldFactory(this.translateService);

  // BEGIN - Area edit form
  areaForm: FormGroup = new FormGroup({});
  areaModel: any = {};
  areaFields: FormlyFieldConfig[] = [];
  areaOptions: FormlyFormOptions = {};
  areaBtnOptions: MatProgressButtonOptions = this.buildSaveButton();
  // END - Area edit form

  // BEGIN - New area form
  newAreaForm: FormGroup = new FormGroup({});
  newAreaModel: any = {};
  newAreaFields: FormlyFieldConfig[] = [];
  newAreaOptions: FormlyFormOptions = {};
  newAreaBtnOptions: MatProgressButtonOptions = this.buildSaveButton();
  // END - New area form

  // BEGIN - New content (section) form
  newContentForm: FormGroup = new FormGroup({});
  newContentModel: any = {};
  newContentFields: FormlyFieldConfig[] = [];
  newContentOptions: FormlyFormOptions = {};
  newContentBtnOptions: MatProgressButtonOptions = this.buildSaveButton();
  // END - New content (section) form

  /**
   * Class constructor
   */
  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    private cookieService: CookieService,
    private documentTemplateService: DocumentTemplateService,
    private themeService: ThemeService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    public dialog: MatDialog
  ) {}

  /**
   * Build a dedicated submit button state. Every form owns its own instance: sharing
   * one makes the spinner of a form spin while another one is saving.
   *
   * @since 3.0.0
   */
  private buildSaveButton(): MatProgressButtonOptions {
    return {
      active: false,
      text: this.translateService.instant("common.cta.save"),
      spinnerSize: 19,
      raised: true,
      stroked: false,
      flat: false,
      fab: false,
      buttonColor: "primary",
      spinnerColor: "primary",
      fullWidth: false,
      disabled: false,
      mode: "indeterminate",
      buttonIcon: {
        fontSet: "fal",
        fontIcon: "fa-save",
        inline: true,
      },
    };
  }

  /**
   * Init component
   *
   * @since 3.0.0
   */
  ngOnInit(): void {
    this.themeService.setPageTitle(
      this.translateService.instant("settings.documentTemplate.pageTitle")
    );

    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res || [])
      )
    );

    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;
  }

  /**
   * After view init logic
   *
   * @since 3.0.0
   */
  ngAfterViewInit(): void {
    this.loadAreas();
    this.loadCondoStructures();
  }

  /**
   * Handle component destroy
   *
   * @since 3.0.0
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // BEGIN - Loading

  /**
   * Load level 1. Nothing else is fetched: sections arrive only when an area is
   * picked, documents only when the dialog opens.
   *
   * @since 3.0.0
   */
  loadAreas(): void {
    this.areasAreLoading = true;
    this.subscriptions.push(
      this.documentTemplateService
        .listAreas(this.includeDeleted)
        .pipe(finalize(() => (this.areasAreLoading = false)))
        .subscribe(
          (res) => {
            // Already ordered by the back-end: the front-end never sorts the level
            this.areas = res && Array.isArray(res.data) ? res.data : [];
            this.applyAreaSearch();
            this.refreshSelectedArea();
            this.loadAreasForValidation();
          },
          (err) => {
            this.documentTemplateService.handleApiError(err);
          }
        )
    );
  }

  /**
   * Refresh the snapshot the area label check runs against.
   *
   * Hidden areas have to be in it: the back-end refuses a label held by a soft deleted
   * area too, with its own wording, and that is the area the user cannot see. When the
   * displayed list already carries them there is nothing to fetch.
   *
   * @since 3.0.0
   */
  private loadAreasForValidation(): void {
    if (this.includeDeleted) {
      this.areasForValidation = this.areas;
      this.areaCheckIsDegraded = false;
      return;
    }
    this.subscriptions.push(
      this.documentTemplateService.listAreas(true).subscribe(
        (res) => {
          this.areasForValidation =
            res && Array.isArray(res.data) ? res.data : [];
          this.areaCheckIsDegraded = false;
        },
        () => {
          // Never silent: the form says the check is unavailable, and the back-end
          // keeps refusing the duplicates it knows about.
          this.areasForValidation = this.areas;
          this.areaCheckIsDegraded = true;
        }
      )
    );
  }

  /**
   * Load the sections of the given area (level 2).
   *
   * @since 3.0.0
   */
  loadContents(areaId: string): void {
    if (!areaId) {
      this.contents = [];
      this.contentsForValidation = [];
      return;
    }
    this.contentsAreLoading = true;
    this.subscriptions.push(
      this.documentTemplateService
        .listContents(areaId, this.includeDeleted)
        .pipe(finalize(() => (this.contentsAreLoading = false)))
        .subscribe(
          (res) => {
            this.contents = res && Array.isArray(res.data) ? res.data : [];
            this.loadContentsForValidation(areaId);
          },
          (err) => {
            this.contents = [];
            this.documentTemplateService.handleApiError(err, {
              onNotFound: () => this.loadAreas(),
            });
          }
        )
    );
  }

  /**
   * Refresh the snapshot the section label check runs against. @see
   * loadAreasForValidation: the section labels are unique inside their own area only.
   *
   * @since 3.0.0
   */
  private loadContentsForValidation(areaId: string): void {
    if (this.includeDeleted) {
      this.contentsForValidation = this.contents;
      this.contentCheckIsDegraded = false;
      return;
    }
    this.subscriptions.push(
      this.documentTemplateService.listContents(areaId, true).subscribe(
        (res) => {
          this.contentsForValidation =
            res && Array.isArray(res.data) ? res.data : [];
          this.contentCheckIsDegraded = false;
        },
        () => {
          this.contentsForValidation = this.contents;
          this.contentCheckIsDegraded = true;
        }
      )
    );
  }

  /**
   * Load the condo structures feeding the "structure" select of an array section.
   * The list is rebuilt from scratch on every call: appending to it is exactly the
   * bug that makes the legacy page accumulate duplicated options.
   *
   * @since 3.0.0
   */
  loadCondoStructures(): void {
    this.subscriptions.push(
      this.documentTemplateService.getCondoStructures().subscribe(
        (res) => {
          this.condoStructures =
            this.documentTemplateService.extractStructureOptions(res);
        },
        (err) => {
          this.condoStructures = [];
          this.documentTemplateService.handleApiError(err);
        }
      )
    );
  }

  /**
   * Re-sync the selected area (and its editor) with the freshly loaded list, or drop
   * the selection when the area is gone.
   *
   * @since 3.0.0
   */
  private refreshSelectedArea(): void {
    if (!this.selectedArea) {
      return;
    }
    const found = this.areas.find(
      (area) => area._id.toString() === this.selectedArea._id.toString()
    );
    if (!found) {
      this.selectedArea = null;
      this.contents = [];
      this.rightPanel = "empty";
      this.newContentIsVisible = false;
      return;
    }
    this.selectedArea = found;
    if (this.rightPanel === "area") {
      this.buildAreaFields();
    }
  }

  // END - Loading

  // BEGIN - Filters

  /**
   * Toggle the "show hidden" filter: it drives includeDeleted on every list.
   *
   * @since 3.0.0
   */
  onIncludeDeletedChange(): void {
    this.loadAreas();
    if (this.selectedArea) {
      this.loadContents(this.selectedArea._id);
    }
  }

  /**
   * Filter the areas list client side (the level holds 23 rows, the back-end has no
   * search on it).
   *
   * @since 3.0.0
   */
  applyAreaSearch(): void {
    const query = (this.areaSearch || "").trim().toLowerCase();
    if (!query) {
      this.visibleAreas = this.areas.slice();
      return;
    }
    // `title` is deliberately out of the search: it is an internal identifier the user
    // never sees, and a row matching on it would look like a result out of nowhere
    this.visibleAreas = this.areas.filter((area) => {
      const italian = area.translation ? area.translation.it || "" : "";
      const english = area.translation ? area.translation.en || "" : "";
      return (
        italian.toLowerCase().indexOf(query) >= 0 ||
        english.toLowerCase().indexOf(query) >= 0 ||
        (area.code || "").toLowerCase().indexOf(query) >= 0
      );
    });
  }

  /**
   * Reordering is only safe on the full list: the drop indexes refer to the FILTERED
   * list, while what gets sent is the whole sequence of ids, so with a filter on the
   * two would not describe the same order.
   *
   * @since 3.0.0
   */
  get areaDragIsDisabled(): boolean {
    return (
      this.reorderIsSaving || !!(this.areaSearch && this.areaSearch.trim())
    );
  }

  // END - Filters

  // BEGIN - Level 1: selection and editing

  /**
   * Open an area: loads its sections and builds its editor.
   *
   * @since 3.0.0
   */
  onAreaClicked(area: DocumentTemplateArea): void {
    this.selectedArea = area;
    this.rightPanel = "area";
    this.newContentIsVisible = false;
    this.areaIdentifiersUnlocked = false;
    this.resetNewContentForm();
    this.buildAreaFields();
    this.loadContents(area._id);
  }

  /**
   * Build the area editor. Identifiers are part of the fields only once unlocked, so
   * that a locked form can never send them.
   *
   * @since 3.0.0
   */
  private buildAreaFields(): void {
    const area = this.selectedArea;
    if (!area) {
      return;
    }

    this.areaForm = new FormGroup({});
    this.areaModel = {
      code: area.code,
      group: area.group,
      type: area.type,
      visible: !!area.visible,
      expanded: !!area.expanded,
      translation: {
        it: area.translation ? area.translation.it : "",
        en: area.translation ? area.translation.en : "",
      },
    };

    const fields: FormlyFieldConfig[] = [];
    if (this.areaIdentifiersUnlocked) {
      fields.push({
        fieldGroupClassName: "row",
        fieldGroup: [this.fieldFactory.areaCode("col-12 col-md-4")],
      });
    }
    fields.push({
      fieldGroupClassName: "row",
      fieldGroup: [
        this.fieldFactory.translation(
          "it",
          "col-12 col-md-6",
          this.buildAreaLabelChecker("it", area._id)
        ),
        this.fieldFactory.translation(
          "en",
          "col-12 col-md-6",
          this.buildAreaLabelChecker("en", area._id)
        ),
        this.fieldFactory.areaGroup("col-12 col-md-6"),
        this.fieldFactory.areaType("col-12 col-md-6"),
        this.fieldFactory.toggle(
          "visible",
          "settings.documentTemplate.fields.visible",
          "col-12 col-md-6"
        ),
        this.fieldFactory.toggle(
          "expanded",
          "settings.documentTemplate.fields.expanded",
          "col-12 col-md-6"
        ),
      ],
    });

    this.areaFields = fields;
  }

  /**
   * Ask for an explicit confirmation before letting the user touch `code`: it ends up
   * in already uploaded documents, in routes and in the QR codes already printed and
   * glued on the buildings.
   *
   * @since 3.0.0
   */
  onUnlockAreaIdentifiers(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant(
          "settings.documentTemplate.dialogs.unlockIdentifier.title"
        ),
        message: this.translateService.instant(
          "settings.documentTemplate.dialogs.unlockIdentifier.message"
        ),
        btnOkText: this.translateService.instant(
          "settings.documentTemplate.actions.unlockIdentifier"
        ),
        btnCancelText: this.translateService.instant("common.cta.undo"),
        btnOkColor: "warn",
        btnOkIcon: "fa-unlock",
      },
      width: "560px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.areaIdentifiersUnlocked = true;
        this.buildAreaFields();
      });
  }

  /**
   * Save the area. Only the fields that really changed are sent, as the back-end
   * expects a partial payload.
   *
   * @since 3.0.0
   */
  onAreaSubmit(): void {
    if (!this.selectedArea) {
      return;
    }
    if (!this.markFormAsTouched(this.areaForm)) {
      return;
    }

    const payload = this.buildAreaUpdatePayload();
    if (!payload) {
      this.toastrService.info(
        this.translateService.instant("settings.documentTemplate.noChanges")
      );
      return;
    }

    const conflictFields = this.collectConflictFields(payload);

    // Touching an identifier is destructive outside this application: confirm again
    // right before writing it. The labels are not identifiers and go straight through.
    if (typeof payload.code !== "undefined") {
      this.confirmThen(
        this.translateService.instant(
          "settings.documentTemplate.dialogs.saveIdentifier.title"
        ),
        this.translateService.instant(
          "settings.documentTemplate.dialogs.saveIdentifier.message"
        ),
        () => this.persistArea(payload, conflictFields)
      );
      return;
    }
    this.persistArea(payload, conflictFields);
  }

  /**
   * Perform the area PUT.
   *
   * @since 3.0.0
   */
  private persistArea(
    payload: DocumentTemplateAreaUpdatePayload,
    conflictFields: string[]
  ): void {
    this.areaBtnOptions.active = true;
    this.subscriptions.push(
      this.documentTemplateService
        .updateArea(this.selectedArea._id, payload)
        .pipe(finalize(() => (this.areaBtnOptions.active = false)))
        .subscribe(
          (res) => {
            this.toastrService.success(
              res && res.message
                ? res.message
                : this.translateService.instant(
                    "settings.documentTemplate.saved"
                  )
            );
            this.areaIdentifiersUnlocked = false;
            // Reloads the validation snapshot too: the label just saved is now taken
            this.loadAreas();
          },
          (err) => {
            this.documentTemplateService.handleApiError(err, {
              form: this.areaForm,
              conflictFields,
              onNotFound: () => this.loadAreas(),
            });
          }
        )
    );
  }

  /**
   * Diff the area editor against the loaded area, so that the PUT carries only what
   * changed. Returns null when nothing changed.
   *
   * @since 3.0.0
   */
  private buildAreaUpdatePayload(): DocumentTemplateAreaUpdatePayload {
    const area = this.selectedArea;
    const model = this.areaModel || {};
    const payload: DocumentTemplateAreaUpdatePayload = {};
    let changed = false;

    if (this.areaIdentifiersUnlocked && model.code !== area.code) {
      payload.code = model.code;
      changed = true;
    }
    if (model.group !== area.group) {
      payload.group = model.group;
      changed = true;
    }
    if (model.type !== area.type) {
      payload.type = model.type;
      changed = true;
    }
    if (!!model.visible !== !!area.visible) {
      payload.visible = !!model.visible;
      changed = true;
    }
    if (!!model.expanded !== !!area.expanded) {
      payload.expanded = !!model.expanded;
      changed = true;
    }

    const translation = this.buildTranslationPayload(
      model.translation,
      area.translation
    );
    if (translation) {
      payload.translation = translation;
      changed = true;
    }

    return changed ? payload : null;
  }

  /**
   * Open the "new area" editor. Its form and model are dedicated: sharing them with
   * the detail editor is what makes the legacy page leak values between the two.
   *
   * @since 3.0.0
   */
  onAddAreaClicked(): void {
    this.rightPanel = "newArea";
    this.newContentIsVisible = false;
    this.newAreaForm = new FormGroup({});
    this.newAreaModel = {
      code: suggestAreaCode(this.areas.map((area) => area.code)),
      group: "amministrativo",
      type: "accordion",
      visible: true,
      expanded: false,
      translation: { it: "", en: "" },
    };
    this.newAreaFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          this.fieldFactory.areaCode("col-12 col-md-4"),
          this.fieldFactory.translation(
            "it",
            "col-12 col-md-4",
            this.buildAreaLabelChecker("it", "")
          ),
          this.fieldFactory.translation(
            "en",
            "col-12 col-md-4",
            this.buildAreaLabelChecker("en", "")
          ),
          this.fieldFactory.areaGroup("col-12 col-md-6"),
          this.fieldFactory.areaType("col-12 col-md-6"),
          this.fieldFactory.toggle(
            "visible",
            "settings.documentTemplate.fields.visible",
            "col-12 col-md-6"
          ),
          this.fieldFactory.toggle(
            "expanded",
            "settings.documentTemplate.fields.expanded",
            "col-12 col-md-6"
          ),
        ],
      },
    ];
  }

  /**
   * Create the area. `position` is omitted: the back-end appends it at the end, and
   * `title` is omitted too: the back-end generates it out of the english label.
   *
   * @since 3.0.0
   */
  onNewAreaSubmit(): void {
    if (!this.markFormAsTouched(this.newAreaForm)) {
      return;
    }
    const model = this.newAreaModel || {};
    this.newAreaBtnOptions.active = true;
    this.subscriptions.push(
      this.documentTemplateService
        .createArea({
          code: model.code,
          group: model.group,
          type: model.type,
          visible: !!model.visible,
          expanded: !!model.expanded,
          translation: {
            it: model.translation ? model.translation.it : "",
            en: model.translation ? model.translation.en : "",
          },
        })
        .pipe(finalize(() => (this.newAreaBtnOptions.active = false)))
        .subscribe(
          (res) => {
            this.toastrService.success(
              res && res.message
                ? res.message
                : this.translateService.instant(
                    "settings.documentTemplate.created"
                  )
            );
            this.loadAreas();
            const created = res && res.data ? res.data : null;
            if (created && created._id) {
              // Jump straight into the freshly created area, ready for its sections
              this.onAreaClicked(created);
            } else {
              this.rightPanel = "empty";
            }
          },
          (err) => {
            this.documentTemplateService.handleApiError(err, {
              form: this.newAreaForm,
              conflictFields: ["code", "translation.it", "translation.en"],
            });
          }
        )
    );
  }

  /**
   * Hide an area. The delete cascades on its sections and documents, so the exact
   * amount of nodes about to disappear is computed first (a single /tree call) and
   * shown inside the confirmation.
   *
   * @since 3.0.0
   */
  onDeleteArea(area: DocumentTemplateArea): void {
    this.previewIsLoading = true;
    this.subscriptions.push(
      this.documentTemplateService
        .getTree(false)
        .pipe(finalize(() => (this.previewIsLoading = false)))
        .subscribe(
          (res) => {
            this.confirmDeleteArea(area, this.countAreaSubtree(res, area._id));
          },
          () => {
            // The preview is a nicety: a failure must not block the deletion, it only
            // downgrades the confirmation to its generic wording.
            this.confirmDeleteArea(area, null);
          }
        )
    );
  }

  /**
   * Count the sections and documents a cascading delete would hide.
   *
   * @since 3.0.0
   */
  private countAreaSubtree(
    res: any,
    areaId: string
  ): { contents: number; docs: number } {
    const areas =
      res && res.data && Array.isArray(res.data.area) ? res.data.area : [];
    const area = areas.find(
      (item: DocumentTemplateTreeArea) =>
        item && item._id && item._id.toString() === areaId.toString()
    );
    if (!area || !Array.isArray(area.content)) {
      return { contents: 0, docs: 0 };
    }
    let docs = 0;
    area.content.forEach((content: any) => {
      if (content && Array.isArray(content.documents)) {
        docs += content.documents.length;
      }
    });
    return { contents: area.content.length, docs };
  }

  /**
   * Confirmation of the area deletion, then the DELETE itself.
   *
   * @since 3.0.0
   */
  private confirmDeleteArea(
    area: DocumentTemplateArea,
    counts: { contents: number; docs: number }
  ): void {
    const label = this.nodeLabel(area);
    const message = counts
      ? this.translateService.instant(
          "settings.documentTemplate.dialogs.deleteArea.messageWithCounts",
          { name: label, contents: counts.contents, docs: counts.docs }
        )
      : this.translateService.instant(
          "settings.documentTemplate.dialogs.deleteArea.message",
          { name: label }
        );

    this.confirmThen(
      this.translateService.instant(
        "settings.documentTemplate.dialogs.deleteArea.title"
      ),
      message,
      () => {
        this.subscriptions.push(
          this.documentTemplateService.deleteArea(area._id).subscribe(
            (res) => {
              this.notifyDeleteResult(res);
              this.loadAreas();
              if (
                this.selectedArea &&
                this.selectedArea._id.toString() === area._id.toString()
              ) {
                this.loadContents(area._id);
              }
            },
            (err) => {
              this.documentTemplateService.handleApiError(err, {
                onNotFound: () => this.loadAreas(),
              });
            }
          )
        );
      },
      "warn"
    );
  }

  /**
   * Restore a hidden area. The restore is NOT cascading: its sections and documents
   * stay hidden and have to be restored one by one.
   *
   * @since 3.0.0
   */
  onRestoreArea(area: DocumentTemplateArea): void {
    this.subscriptions.push(
      this.documentTemplateService.restoreArea(area._id).subscribe(
        (res) => {
          // Body carries the "restore is not cascading" reminder, title the back-end
          // message: toastr renders the title on top, so the short text goes there.
          this.toastrService.success(
            this.translateService.instant(
              "settings.documentTemplate.restoreNotCascadingHint"
            ),
            res && res.message
              ? res.message
              : this.translateService.instant(
                  "settings.documentTemplate.restored"
                )
          );
          this.loadAreas();
          if (
            this.selectedArea &&
            this.selectedArea._id.toString() === area._id.toString()
          ) {
            this.loadContents(area._id);
          }
        },
        (err) => {
          this.documentTemplateService.handleApiError(err, {
            onNotFound: () => this.loadAreas(),
          });
        }
      )
    );
  }

  /**
   * Persist the new order of level 1. One PUT on the moved node when the sparse
   * numbering leaves room between its neighbours, a full renumbering otherwise.
   *
   * @since 3.0.0
   */
  onAreaDropped(event: CdkDragDrop<DocumentTemplateArea[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    // Optimistic reorder, then let the back-end persist the whole sequence at once.
    moveItemInArray(this.areas, event.previousIndex, event.currentIndex);
    this.applyAreaSearch();

    this.reorderIsSaving = true;
    this.subscriptions.push(
      this.documentTemplateService
        .reorderAreas(
          this.areas.map((area) => area._id),
          this.includeDeleted
        )
        .pipe(finalize(() => (this.reorderIsSaving = false)))
        .subscribe(
          (res) => {
            // The response IS the level, already sorted: take it as the truth
            if (res && Array.isArray(res.data)) {
              this.areas = res.data;
              this.applyAreaSearch();
              this.syncSelectedArea();
            }
            this.toastrService.success(
              this.translateService.instant(
                "settings.documentTemplate.reordered"
              )
            );
          },
          (err) => {
            this.documentTemplateService.handleApiError(err);
            this.loadAreas();
          }
        )
    );
  }

  /**
   * Re-point the selected area at its instance inside a freshly returned list, WITHOUT
   * rebuilding the editor: a reorder must not throw away what the user is typing.
   *
   * @since 3.0.0
   */
  private syncSelectedArea(): void {
    if (!this.selectedArea) {
      return;
    }
    const found = this.areas.find(
      (area) => area._id.toString() === this.selectedArea._id.toString()
    );
    if (found) {
      this.selectedArea = found;
    }
  }

  // END - Level 1: selection and editing

  // BEGIN - Level 2: sections

  /**
   * Open the third level dialog of a section. The dialog owns the section editor too
   * (translation, structure, visibility), exactly like the legacy page.
   *
   * @since 3.0.0
   */
  onContentClicked(content: DocumentTemplateContent): void {
    // Angular CDK 8's drag&drop computes pointer position relative to the window scroll
    // captured when the drag starts; if the page behind the dialog is scrolled down,
    // that offset desyncs the calculation and breaks reordering inside the dialog.
    window.scrollTo(0, 0);
    const dialogRef = this.dialog.open(DocumentTemplateDocsDialogComponent, {
      data: {
        content,
        area: this.selectedArea,
        contents: this.contents,
        condoStructures: this.condoStructures,
        includeDeleted: this.includeDeleted,
      },
      width: "90%",
      maxWidth: "90vw",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        // Only the section list can have changed from in there: reload that one level.
        if (res && res.changed && this.selectedArea) {
          this.loadContents(this.selectedArea._id);
        }
      });
  }

  /**
   * Open the "new section" editor, with its own form instance.
   *
   * @since 3.0.0
   */
  onAddContentClicked(): void {
    if (!this.selectedArea) {
      return;
    }
    this.newContentIsVisible = true;
    this.resetNewContentForm();
  }

  /**
   * (Re)build the "new section" form from scratch.
   *
   * @since 3.0.0
   */
  private resetNewContentForm(): void {
    this.newContentForm = new FormGroup({});
    this.newContentModel = {
      code: "",
      elementType: "object",
      structure: "",
      visible: true,
      expanded: false,
      translation: { it: "", en: "" },
    };
    this.newContentFields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          this.fieldFactory.contentCode("col-12 col-md-4"),
          this.fieldFactory.translation(
            "it",
            "col-12 col-md-4",
            this.buildContentLabelChecker("it", "")
          ),
          this.fieldFactory.translation(
            "en",
            "col-12 col-md-4",
            this.buildContentLabelChecker("en", "")
          ),
          this.fieldFactory.elementType("col-12 col-md-6"),
          this.fieldFactory.structure(
            "col-12 col-md-6",
            this.buildStructureOptions(),
            () => this.newContentModel
          ),
          this.fieldFactory.toggle(
            "visible",
            "settings.documentTemplate.fields.visible",
            "col-12 col-md-6"
          ),
          this.fieldFactory.toggle(
            "expanded",
            "settings.documentTemplate.fields.expanded",
            "col-12 col-md-6"
          ),
        ],
      },
    ];
  }

  /**
   * Create the section under the selected area. `title` is omitted: the back-end
   * generates it out of the english label alone.
   *
   * @since 3.0.0
   */
  onNewContentSubmit(): void {
    if (!this.selectedArea) {
      return;
    }
    if (!this.markFormAsTouched(this.newContentForm)) {
      return;
    }
    const model = this.newContentModel || {};
    const isArray = model.elementType === "array";
    if (isArray && !model.structure) {
      const control = this.newContentForm.get("structure");
      if (control) {
        control.markAsTouched();
        control.setErrors({
          "server-error": this.translateService.instant(
            "common.errors.field_is_required"
          ),
        });
      }
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    this.newContentBtnOptions.active = true;
    this.subscriptions.push(
      this.documentTemplateService
        .createContent({
          areaId: this.selectedArea._id,
          code: model.code,
          elementType: model.elementType,
          structure: isArray ? model.structure : "",
          visible: !!model.visible,
          expanded: !!model.expanded,
          translation: {
            it: model.translation ? model.translation.it : "",
            en: model.translation ? model.translation.en : "",
          },
        })
        .pipe(finalize(() => (this.newContentBtnOptions.active = false)))
        .subscribe(
          (res) => {
            this.toastrService.success(
              res && res.message
                ? res.message
                : this.translateService.instant(
                    "settings.documentTemplate.created"
                  )
            );
            this.newContentIsVisible = false;
            this.loadContents(this.selectedArea._id);
          },
          (err) => {
            this.documentTemplateService.handleApiError(err, {
              form: this.newContentForm,
              conflictFields: ["code", "translation.it", "translation.en"],
            });
          }
        )
    );
  }

  /**
   * Hide a section. The delete cascades on its documents, counted first.
   *
   * @since 3.0.0
   */
  onDeleteContent(content: DocumentTemplateContent): void {
    this.previewIsLoading = true;
    this.subscriptions.push(
      this.documentTemplateService
        .listDocs(content._id, false)
        .pipe(finalize(() => (this.previewIsLoading = false)))
        .subscribe(
          (res) => {
            const docs = res && Array.isArray(res.data) ? res.data.length : 0;
            this.confirmDeleteContent(content, docs);
          },
          () => {
            this.confirmDeleteContent(content, null);
          }
        )
    );
  }

  /**
   * Confirmation of the section deletion, then the DELETE itself.
   *
   * @since 3.0.0
   */
  private confirmDeleteContent(
    content: DocumentTemplateContent,
    docs: number
  ): void {
    const label = this.nodeLabel(content);
    const message =
      docs === null
        ? this.translateService.instant(
            "settings.documentTemplate.dialogs.deleteContent.message",
            { name: label }
          )
        : this.translateService.instant(
            "settings.documentTemplate.dialogs.deleteContent.messageWithCounts",
            { name: label, docs }
          );

    this.confirmThen(
      this.translateService.instant(
        "settings.documentTemplate.dialogs.deleteContent.title"
      ),
      message,
      () => {
        this.subscriptions.push(
          this.documentTemplateService.deleteContent(content._id).subscribe(
            (res) => {
              this.notifyDeleteResult(res);
              this.loadContents(this.selectedArea._id);
            },
            (err) => {
              this.documentTemplateService.handleApiError(err, {
                onNotFound: () => this.loadContents(this.selectedArea._id),
              });
            }
          )
        );
      },
      "warn"
    );
  }

  /**
   * Restore a hidden section. Blocked (and the button disabled) while its area is
   * hidden: the back-end answers 400 in that case.
   *
   * @since 3.0.0
   */
  onRestoreContent(content: DocumentTemplateContent): void {
    this.subscriptions.push(
      this.documentTemplateService.restoreContent(content._id).subscribe(
        (res) => {
          // Body carries the "restore is not cascading" reminder, title the back-end
          // message: toastr renders the title on top, so the short text goes there.
          this.toastrService.success(
            this.translateService.instant(
              "settings.documentTemplate.restoreNotCascadingHint"
            ),
            res && res.message
              ? res.message
              : this.translateService.instant(
                  "settings.documentTemplate.restored"
                )
          );
          this.loadContents(this.selectedArea._id);
        },
        (err) => {
          this.documentTemplateService.handleApiError(err, {
            onNotFound: () => this.loadContents(this.selectedArea._id),
          });
        }
      )
    );
  }

  /**
   * True when the section cannot be restored because its area is still hidden.
   *
   * @since 3.0.0
   */
  restoreContentIsBlocked(): boolean {
    return !!(this.selectedArea && this.selectedArea.deleted);
  }

  /**
   * Move a section into another area: a single PUT with `{ areaId }`. Without a
   * position the section lands at the end of the destination.
   *
   * The labels of a section are unique INSIDE ITS AREA, so a destination already
   * holding a section with the same name refuses the move with a 409. The sections of
   * the other areas are not in memory: the whole tree is fetched once, on demand, only
   * for this deliberate action, and the destinations that would be refused are offered
   * disabled instead of letting the user discover it after the fact.
   *
   * @since 3.0.0
   */
  onMoveContent(content: DocumentTemplateContent): void {
    this.moveIsLoading = true;
    this.subscriptions.push(
      this.documentTemplateService
        .getTree(true)
        .pipe(finalize(() => (this.moveIsLoading = false)))
        .subscribe(
          (res) => {
            this.openMoveContentDialog(content, this.readTreeAreas(res));
          },
          () => {
            // The check is a nicety: a failure must not block the move, the back-end
            // stays the one refusing a destination whose name is taken.
            this.openMoveContentDialog(content, null);
          }
        )
    );
  }

  /**
   * Sections of every area, indexed by area id, out of a GET /tree response.
   *
   * @since 3.0.0
   */
  private readTreeAreas(res: any): DocumentTemplateTreeArea[] {
    return res && res.data && Array.isArray(res.data.area) ? res.data.area : [];
  }

  /**
   * Open the destination picker of a section move and perform the move.
   *
   * @since 3.0.0
   *
   * @param content   Section being moved
   * @param treeAreas Whole tree, or null when it could not be fetched: every
   *                  destination is then offered, and the back-end arbitrates
   */
  private openMoveContentDialog(
    content: DocumentTemplateContent,
    treeAreas: DocumentTemplateTreeArea[]
  ): void {
    const takenHint = this.translateService.instant(
      "settings.documentTemplate.move.destLabelTaken"
    );
    const options = this.areas
      .filter(
        (area) => area._id.toString() !== this.selectedArea._id.toString()
      )
      .map((area) => {
        const taken = treeAreas
          ? this.labelIsTakenInArea(content, area._id, treeAreas)
          : false;
        return {
          value: area._id,
          label: this.nodeLabel(area),
          hint: taken ? `${area.code} — ${takenHint}` : area.code,
          disabled: taken,
        };
      });

    const data: DocumentTemplateMoveDialogData = {
      title: this.translateService.instant(
        "settings.documentTemplate.move.contentTitle"
      ),
      subject: this.nodeLabel(content),
      currentLabel: this.nodeLabel(this.selectedArea),
      selectLabel: this.translateService.instant(
        "settings.documentTemplate.move.destArea"
      ),
      warning: this.translateService.instant(
        "settings.documentTemplate.move.warning"
      ),
      groups: [{ label: "", options }],
    };

    const dialogRef = this.dialog.open(DocumentTemplateMoveDialogComponent, {
      data,
      width: "560px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((destAreaId) => {
        if (!destAreaId) {
          return;
        }
        const payload: DocumentTemplateContentUpdatePayload = {
          areaId: destAreaId,
        };
        this.subscriptions.push(
          this.documentTemplateService
            .updateContent(content._id, payload)
            .subscribe(
              (res) => {
                this.toastrService.success(
                  res && res.message
                    ? res.message
                    : this.translateService.instant(
                        "settings.documentTemplate.moved"
                      )
                );
                this.loadContents(this.selectedArea._id);
              },
              (err) => {
                this.documentTemplateService.handleApiError(err, {
                  onNotFound: () => this.loadContents(this.selectedArea._id),
                });
              }
            )
        );
      });
  }

  /**
   * Persist the new order of level 2.
   *
   * @since 3.0.0
   */
  onContentDropped(event: CdkDragDrop<DocumentTemplateContent[]>): void {
    if (event.previousIndex === event.currentIndex || !this.selectedArea) {
      return;
    }
    moveItemInArray(this.contents, event.previousIndex, event.currentIndex);

    this.reorderIsSaving = true;
    this.subscriptions.push(
      this.documentTemplateService
        .reorderContents(
          this.selectedArea._id,
          this.contents.map((content) => content._id),
          this.includeDeleted
        )
        .pipe(finalize(() => (this.reorderIsSaving = false)))
        .subscribe(
          (res) => {
            if (res && Array.isArray(res.data)) {
              this.contents = res.data;
            }
            this.toastrService.success(
              this.translateService.instant(
                "settings.documentTemplate.reordered"
              )
            );
          },
          (err) => {
            this.documentTemplateService.handleApiError(err);
            this.loadContents(this.selectedArea._id);
          }
        )
    );
  }

  // END - Level 2: sections

  // BEGIN - Formly field factories

  /**
   * Structure options rebuilt from scratch on every call. Appending to a shared array
   * is exactly what makes the legacy page accumulate duplicated options.
   *
   * @since 3.0.0
   */
  private buildStructureOptions(): FieldOption[] {
    return this.condoStructures.map((structure) => ({
      value: structure.key,
      label: structure.translation
        ? structure.translation.it || structure.key
        : structure.key,
    }));
  }

  // END - Formly field factories

  // BEGIN - Duplicated labels

  /**
   * Label check of an area: the italian and the english labels are unique across ALL
   * the areas, hidden ones included.
   *
   * The snapshot is read when the validator runs, not when it is built, so a form left
   * open while another area is created still validates against the current state.
   *
   * @since 3.0.0
   *
   * @param language    Label being validated
   * @param [excludeId] Area being edited, which must not collide with itself
   */
  private buildAreaLabelChecker(
    language: "it" | "en",
    excludeId: string
  ): LabelConflictChecker {
    return (value: string): string =>
      this.buildConflictMessage(
        findLabelConflict(this.areasForValidation, language, value, excludeId),
        "Area"
      );
  }

  /**
   * Label check of a section: unique INSIDE ITS AREA only. Two areas legitimately hold
   * a section with the same name ("Sicurezza" lives in more than one area).
   *
   * @since 3.0.0
   */
  private buildContentLabelChecker(
    language: "it" | "en",
    excludeId: string
  ): LabelConflictChecker {
    return (value: string): string =>
      this.buildConflictMessage(
        findLabelConflict(
          this.contentsForValidation,
          language,
          value,
          excludeId
        ),
        "Content"
      );
  }

  /**
   * Wording of a label conflict, '' when there is none.
   *
   * A hidden owner gets its own message: the user does not see that node in the tree,
   * so the name looks free and a plain "already used" would read as a bug.
   *
   * @since 3.0.0
   *
   * @param conflict Colliding node, or null
   * @param level    'Area' or 'Content', to pick the wording of the right level
   */
  private buildConflictMessage(conflict: LabelConflict, level: string): string {
    if (!conflict) {
      return "";
    }
    const key = conflict.deleted
      ? `settings.documentTemplate.errors.duplicate${level}Hidden`
      : `settings.documentTemplate.errors.duplicate${level}`;
    return this.translateService.instant(key, { name: conflict.label });
  }

  /**
   * True when the destination area already holds a section carrying one of the labels
   * of the section being moved: the back-end refuses that move with a 409.
   *
   * @since 3.0.0
   *
   * @param content   Section being moved
   * @param areaId    Candidate destination
   * @param treeAreas Whole tree, hidden nodes included
   */
  private labelIsTakenInArea(
    content: DocumentTemplateContent,
    areaId: string,
    treeAreas: DocumentTemplateTreeArea[]
  ): boolean {
    const area = treeAreas.find(
      (item) => item && item._id && item._id.toString() === areaId.toString()
    );
    if (!area || !Array.isArray(area.content) || !area.content.length) {
      return false;
    }
    const translation = content.translation ? content.translation : { it: "", en: "" };
    // The section keeps its own id in another area: nothing to exclude here
    return (
      !!findLabelConflict(area.content, "it", translation.it) ||
      !!findLabelConflict(area.content, "en", translation.en)
    );
  }

  /**
   * Controls of a payload that could be the duplicated one, so that a 409 highlights
   * where to act: the identifier and the two labels, whichever is actually being sent.
   *
   * @since 3.0.0
   */
  private collectConflictFields(
    payload: DocumentTemplateAreaUpdatePayload | DocumentTemplateContentUpdatePayload
  ): string[] {
    const fields: string[] = [];
    if (typeof payload.code !== "undefined") {
      fields.push("code");
    }
    const translation = payload.translation;
    if (translation) {
      if (typeof translation.it !== "undefined") {
        fields.push("translation.it");
      }
      if (typeof translation.en !== "undefined") {
        fields.push("translation.en");
      }
    }
    return fields;
  }

  // END - Duplicated labels

  // BEGIN - Shared helpers

  /**
   * Label of a node in the current interface language, with a fallback on the other
   * language and then on the identifier the user does know (the code, or the document
   * key). `title` is never used: it is an internal identifier the user must not see.
   *
   * @since 3.0.0
   */
  nodeLabel(node: any): string {
    if (!node) {
      return "";
    }
    const translation = node.translation || {};
    if (this.lang === "en" && translation.en) {
      return translation.en;
    }
    if (translation.it) {
      return translation.it;
    }
    if (translation.en) {
      return translation.en;
    }
    return node.code || node.key || "";
  }

  /**
   * Mark every control as touched and refuse an invalid submit, always telling the
   * user why (the legacy page only writes to the console).
   *
   * @since 3.0.0
   *
   * @returns true when the form can be submitted
   */
  private markFormAsTouched(form: FormGroup): boolean {
    if (!form) {
      return false;
    }
    Object.keys(form.controls).forEach((key) => {
      form.controls[key].markAsTouched();
      form.controls[key].markAsDirty();
    });
    if (form.invalid) {
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return false;
    }
    return true;
  }

  /**
   * Build the translation part of an update payload, keeping only the languages that
   * really changed (the back-end merges, it does not replace).
   *
   * @since 3.0.0
   */
  private buildTranslationPayload(model: any, current: any): any {
    const source = model || {};
    const original = current || {};
    const payload: any = {};
    let changed = false;
    if (source.it !== original.it) {
      payload.it = source.it;
      changed = true;
    }
    if (source.en !== original.en) {
      payload.en = source.en;
      changed = true;
    }
    return changed ? payload : null;
  }

  /**
   * Open a confirmation dialog and run `action` when the user confirms.
   *
   * @since 3.0.0
   */
  private confirmThen(
    title: string,
    message: string,
    action: () => void,
    okColor?: string
  ): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title,
        message,
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
        btnOkColor: okColor || "primary",
      },
      width: "560px",
    });
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          action();
        }
      });
  }

  /**
   * Report what a cascading delete really hid, using the counters of the response.
   *
   * @since 3.0.0
   */
  private notifyDeleteResult(res: any): void {
    const data: DocumentTemplateDeleteResult =
      res && res.data ? res.data : { deletedAreas: 0, deletedContents: 0, deletedDocs: 0 };
    this.toastrService.success(
      this.translateService.instant(
        "settings.documentTemplate.deleteResult",
        {
          areas: data.deletedAreas || 0,
          contents: data.deletedContents || 0,
          docs: data.deletedDocs || 0,
        }
      ),
      res && res.message ? res.message : undefined
    );
  }

  // END - Shared helpers
}
